import { cx } from '@emotion/css';
import { Col } from 'antd';
import { useSelector } from 'react-redux';
import { useSubmittedAccountSelector } from 'src/hooks';
import {
  CurrencyField,
  MButton,
  MFormDateField,
  MFormTextField,
  MModalBody,
  MModalFooter,
  MModalTitle,
  MTooltip,
  MTypography,
  SubmissionResult,
} from 'src/lib';
import { mapOrderDurationDtoToLabel, mapOrderTypeDtoToLabel } from 'src/mappers';
import { Position, SecurityOrderView } from 'src/models';
import { BaseStyle, Spacing } from 'src/styles';
import { getInvestorName } from 'src/utils';

interface TradeSecurityHoldingFailedStepProps {
  position: Position;
  order: SecurityOrderView;
  onLastPrice: () => void;
  onServiceFee: () => void;
  onClose: () => void;
}

export const TradeSecurityHoldingFailedStep = ({
  position,
  order,
  onLastPrice,
  onServiceFee,
  onClose,
}: TradeSecurityHoldingFailedStepProps) => {
  const error = useSelector((state: any) => state.orders.create.error);
  const { account } = useSubmittedAccountSelector();

  const physicalAddress = [
    account.primaryAccountHolder.physicalAddress.address1,
    account.primaryAccountHolder.physicalAddress.address2,
    account.primaryAccountHolder.physicalAddress.city,
    account.primaryAccountHolder.physicalAddress.postalCode,
    account.primaryAccountHolder.physicalAddress.country.value,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <MModalTitle>Security Order Failed</MModalTitle>
      <MModalBody height={900}>
        <Col span={24} className={cx(BaseStyle.level, Spacing.my12)}>
          <SubmissionResult type='error' message={error?.message} />
        </Col>
        <Col span={24} className={cx(BaseStyle.level, Spacing.my24)}>
          <MTypography variant='h6' align='center'>
            {position?.name}
          </MTypography>
        </Col>
        <Col span={24}>
          <MFormTextField label='Investor Name' value={getInvestorName(account)} />
        </Col>
        <Col span={24}>
          <MFormTextField label='Address' value={physicalAddress} />
        </Col>
        <Col span={24}>
          <MFormDateField label='Date' value={new Date().toISOString()} />
        </Col>
        <Col span={24}>
          <MFormTextField label='Order Type' value={order.type ? mapOrderTypeDtoToLabel(order.type) : undefined} />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Last Price
                <MTooltip showIcon onClick={() => onLastPrice()} />
              </>
            }
            value={<CurrencyField value={position.value} />}
          />
        </Col>
        <Col span={24}>
          <MFormTextField label='Shares Held' value={position.quantity} />
        </Col>
        {order.price ? (
          <Col span={24}>
            <MFormTextField label='Limit Price' value={<CurrencyField value={order.price} />} />
          </Col>
        ) : null}
        {order.stop ? (
          <Col span={24}>
            <MFormTextField label='Stop Price' value={<CurrencyField value={order.stop} />} />
          </Col>
        ) : null}
        <Col span={24}>
          <MFormTextField label={`Shares to ${order.action}`} value={order.quantity} />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Order Expiration'
            value={order.duration ? mapOrderDurationDtoToLabel(order.duration) : undefined}
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Service Fee
                <MTooltip showIcon onClick={() => onServiceFee()} />
              </>
            }
            value='$10.00'
          />
        </Col>
        <Col span={24}>
          <MFormTextField label='Estimated Total' value={<CurrencyField value={order.estimatedTotal} />} />
        </Col>
        <Col span={24}></Col>
      </MModalBody>
      <MModalFooter className={BaseStyle.levelRight}>
        <MButton type='tertiary' onClick={onClose}>
          Close
        </MButton>
      </MModalFooter>
    </>
  );
};
